import { FC, ReactNode, useEffect, useState } from 'react';
import { isRoleAllowed } from '@magicbrief/server/src/permissions/utils/is-role-allowed';
import { captureException } from '@sentry/react';
import { toast } from 'react-toastify';
import Trash01 from 'src/assets/svgicons/duotone/trash-01.svg';
import { usePopupWindow } from 'src/utils/usePopupWindow';
import { useBroadcastChannel } from 'src/utils/useBroadcastChannel';
import { Slack } from '../../assets/svgicons/brands';
import { useI18nContext } from '../../i18n/i18n-react';
import { trpc } from '../../lib/trpc';
import { useUserAndOrganisation } from '../../utils/useUserAndOrganisation';
import { AriaButton, type ButtonVariants } from '../Button/Button';
import DeleteIntegrationModal from './components/DeleteIntegrationModal';

type Props = {
  className?: string;
  variant?: ButtonVariants;
  icon?: ReactNode;
  showDisconnectButton?: boolean;
  onSuccess?: () => void;
  invalidate?: boolean;
  children?: ReactNode;
};

const SlackConnect: FC<Props> = ({
  className,
  variant,
  icon = <Slack />,
  showDisconnectButton = false,
  onSuccess,
  invalidate = true,
  children,
}) => {
  const { LL } = useI18nContext();
  const user = useUserAndOrganisation();
  const { openPopupWindow } = usePopupWindow();
  const trpcUtils = trpc.useUtils();
  const { message, closeChannel } = useBroadcastChannel<
    'init' | 'success' | 'error'
  >({
    name: 'slack-auth',
    defaultValue: 'init',
  });

  // currently we only allow one Slack Workspace, so this will suffice for now
  // we may outgrow this logic if we allow many Slack connections in the future
  const slackIntegration = user.data?.slackIntegrations?.[0];
  const isOrganisationWorkspaceConnected =
    slackIntegration != null && !!user.data?.organisation?.hasSlackIntegrations;

  const [showDeleteIntegrationModal, setShowDeleteIntegrationModal] =
    useState(false);

  const permission =
    trpc.permissions.getCurrentUserPermissionForEntity.useQuery(
      {
        entityType: 'Organisation',
        entityUuid: user.data?.organisation.uuid ?? '',
      },
      {
        enabled: !!user.data,
      }
    );

  const isEditor = permission.data?.role
    ? isRoleAllowed(permission.data.role, 'editor')
    : false;

  // Check if the user has successfully connected to Slack in the popup window (in `SlackOauthExchange`)
  useEffect(() => {
    if (message === 'success') {
      if (invalidate) {
        void trpcUtils.user.getUserAndOrganisation.invalidate();
      }
      toast.success(LL.slack.oauth.exchange.success());
      closeChannel();
      onSuccess?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  const generateOauthUri = trpc.slack.generateOauthUri.useMutation({
    onSuccess: (uri) => openPopupWindow({ url: uri }),
    onError: (e) => {
      captureException(e);
      toast.error(
        LL.integrations.slack.connectSlack.error({
          error: 'message' in e ? e.message : 'Unknown error',
        }),
        { className: 'toast-danger' }
      );
    },
  });

  const authenticate = () => {
    if (!isEditor || isOrganisationWorkspaceConnected) {
      return;
    }

    generateOauthUri.mutate(undefined);
  };

  const onDeleteIntegration = () => {
    setShowDeleteIntegrationModal(false);
  };

  return (
    <div className="flex flex-row gap-1">
      <AriaButton
        className={className}
        onPress={authenticate}
        loading={user.isLoading || generateOauthUri.isLoading}
        isDisabled={!isEditor || isOrganisationWorkspaceConnected}
        icon={icon}
        variant={variant}
      >
        {children ??
          (isOrganisationWorkspaceConnected
            ? LL.integrations.slack.connectSlack.ctaConnected()
            : LL.integrations.slack.connectSlack.ctaConnect())}
      </AriaButton>
      {showDisconnectButton && isOrganisationWorkspaceConnected && (
        <>
          <AriaButton
            onPress={() => {
              setShowDeleteIntegrationModal(true);
            }}
            isDisabled={!isEditor || !isOrganisationWorkspaceConnected}
            icon={icon ?? <Trash01 />}
            colour="danger"
            variant="secondary"
          />
          <DeleteIntegrationModal
            integrationUuid={slackIntegration}
            show={showDeleteIntegrationModal}
            onClose={onDeleteIntegration}
          />
        </>
      )}
    </div>
  );
};

export default SlackConnect;
